import React, { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Upload, message, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { getIcons, uploadIcon } from "../actions/stampcard_action";

const fetchIconsByType = async ({ queryKey }) => {
  const [_key, accUrn, type] = queryKey;
  const res = await getIcons({ account_urn: accUrn, type });
  return res || [];
};

const IconSelector = ({ onChange, accUrn, businessSlug, theme }) => {
  // Memoize fetcher so it's not recreated on each render
  const fetchIconsByType = useCallback(async ({ queryKey }) => {
    const [_key, accUrn, type, theme] = queryKey;
    const res = await getIcons({ account_urn: accUrn, type, theme });
    return res || [];
  }, []);

  const targetQuery = useQuery({
    queryKey: ["icons", accUrn, "target", theme], // theme now part of key
    queryFn: fetchIconsByType,
    enabled: !!accUrn && !!theme,
    staleTime: 60 * 3000, // cache for 1 min
  });

  const inkingQuery = useQuery({
    queryKey: ["icons", accUrn, "inking", theme],
    queryFn: fetchIconsByType,
    enabled: !!accUrn && !!theme,
    staleTime: 60 * 3000,
  });

  const [selectedTarget, setSelectedTarget] = useState(null);
  const [selectedInking, setSelectedInking] = useState(null);

  // Notify parent when selections change (but only when they really change)
  useEffect(() => {
    if (onChange) {
      onChange({ target: selectedTarget, inking: selectedInking });
    }
  }, [selectedTarget, selectedInking]);

  const handleSelect = (icon, type) => {
    if (type === "target") setSelectedTarget(icon);
    if (type === "inking") setSelectedInking(icon);
  };

  const handleUpload = async (file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    try {
      const res = await uploadIcon({ data: formData, accUrn, businessSlug });
      const { fileName } = res || {};
      if (!fileName) throw new Error("Upload failed");

      const localUrl = URL.createObjectURL(file);
      const uploadedIcon = { fileName, url: localUrl };

      if (type === "target") {
        targetQuery.refetch(); // Refresh list after upload
        setSelectedTarget(uploadedIcon);
      } else {
        inkingQuery.refetch();
        setSelectedInking(uploadedIcon);
      }

      message.success(`${type} icon uploaded`);
    } catch (err) {
      console.error(err);
      message.error(`Failed to upload ${type} icon`);
    }

    return false;
  };

  const renderIcons = (icons = [], selected, type, isLoading, isError) => {
    if (isLoading) return <p>Loading {type} icons...</p>;
    if (isError) return <p>Error loading {type} icons</p>;

    return (
      <div className="icon-group" style={{ marginBottom: 24 }}>
        <h4>{type === "target" ? "Target Icon" : "Inking Icon"}</h4>

        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={(file) => {
            if (!file.type.startsWith("image/")) {
              message.error("Only image files allowed");
              return Upload.LIST_IGNORE;
            }
            handleUpload(file, type);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />}>Upload {type} icon</Button>
        </Upload>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            maxHeight: "25rem",
            overflow: "scroll",
          }}
        >
          {icons.map((icon, idx) => (
            <img
              key={icon.fileName || idx}
              src={icon.url}
              alt={icon.fileName || `icon-${idx}`}
              onClick={() => handleSelect(icon, type)}
              style={{
                width: 60,
                height: 60,
                cursor: "pointer",
                border:
                  selected?.url === icon.url
                    ? "2px solid #1890ff"
                    : "1px solid #ccc",
                borderRadius: 6,
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="icon-selector">
      {renderIcons(
        targetQuery.data,
        selectedTarget,
        "target",
        targetQuery.isLoading,
        targetQuery.isError
      )}
      {renderIcons(
        inkingQuery.data,
        selectedInking,
        "inking",
        inkingQuery.isLoading,
        inkingQuery.isError
      )}
    </div>
  );
};

export default IconSelector;
